import { API_ENDPOINTS, APIS } from '../../../_config';
import { CallApi } from '../callApi';
import { useDispatch } from 'react-redux';
import { login, updateUser, logout } from '../../slices/UserSlice';
import { STORAGE } from '../../../_config';
import { persistor } from '../../store';
import { jwtDecode } from 'jwt-decode';

export const useUserApi = () => {
  const dispatch = useDispatch();

  // Use expires_at from API if available
  const calculateExpiryDate = (expires_at: string) => {
    try {
      if (expires_at) {
        return new Date(expires_at);
      }
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);
      return expiryDate;
    } catch (error) {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);
      return expiryDate;
    }
  };

  // Fetch user info from /me endpoint and store in redux/localStorage
  const fetchUserInfo = (token: string, onSuccess?: Function, onError?: Function) => {
    if (token) {
      CallApi.GET(API_ENDPOINTS.USER.GET_USER, {}, true)
        .then((response: any) => {
          if (response && response.data) {
            dispatch(updateUser({ user: response.data }));
            const storageData = JSON.parse(localStorage.getItem(STORAGE) || '{}');
            storageData.user = response.data;
            localStorage.setItem(STORAGE, JSON.stringify(storageData));
            if (onSuccess) onSuccess(response.data);
          } else {
            if (onError) onError('Failed to fetch user info');
          }
        })
        .catch((error) => {
          console.error('Error fetching user profile:', error);
          if (onError) onError(error);
        });
    }
  };

  // Handle login with new API response
  const handleLogin = (data: any, onSuccess: Function, onError: Function) => {
    CallApi.POST(API_ENDPOINTS.USER.LOGIN, data)
      .then((response: any) => {
        if (response && response.data && response.data.access_token) {
          const { access_token, expires_at } = response.data;

          dispatch(
            login({
              token: access_token,
              email: data.identifier,
              expires_at: expires_at,
            })
          );

          const storageData = {
            accessToken: access_token,
            user: {}, // Will be updated after fetching user info
            expires_at: expires_at,
          };
          localStorage.setItem(STORAGE, JSON.stringify(storageData));

          // Fetch user info from /me and update redux/localStorage
          fetchUserInfo(access_token, () => {
            onSuccess('Login successful', response.data);
          }, onError);
        } else {
          onError('Login failed');
        }
      })
      .catch((error) => {
        console.error('Login error:', error);
        onError(error);
      });
  };

  // Logout handler: accepts an optional param for all devices
  const handleLogout = async (otherSessions: boolean = false, onSuccess?: Function) => {
    try {
      // Build the logout URL with query param if needed
      let url = APIS.AUTH.SIGNOUT;
      if (otherSessions) {
        url += '?other_sessions=true';
      }
      await CallApi.POST(url, {});
      // Remove tokens from localStorage
      localStorage.removeItem(STORAGE);
      // Clear Redux state with the logout action
      dispatch(logout());
      // Clear Redux persist
      persistor.purge();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return {
    login: handleLogin,
    fetchUserInfo,
    logout: handleLogout
  };
};
